import React, { useState } from 'react';
import LegalLinks from '@/components/LegalLinks';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

const STRIPE_ELEMENT_STYLE = {
  style: {
    base: {
      color: 'rgba(255, 243, 225, 0.82)',
      fontFamily: "'EB Garamond', Georgia, serif",
      fontSize: '17px',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: 'rgba(255, 243, 225, 0.25)' },
      iconColor: 'rgba(196, 137, 74, 0.6)',
    },
    invalid: {
      color: 'rgba(196, 137, 74, 0.9)',
      iconColor: 'rgba(196, 137, 74, 0.9)',
    },
  },
};

const fieldBase = {
  background: 'rgba(255, 243, 225, 0.04)',
  border: '1px solid rgba(255, 243, 225, 0.10)',
  borderRadius: '10px',
  padding: '14px 16px',
  transition: 'border-color 240ms',
};

const fieldFocused = {
  ...fieldBase,
  border: '1px solid rgba(196, 137, 74, 0.55)',
};

const textInputStyle = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'rgba(255, 243, 225, 0.82)',
  fontFamily: "'EB Garamond', Georgia, serif",
  fontSize: '17px',
  boxSizing: 'border-box',
};

const AUTOFILL_OVERRIDE = `
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px #1a1714 inset;
    -webkit-text-fill-color: rgba(255, 243, 225, 0.82);
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ display: 'inline', marginRight: '5px', verticalAlign: 'middle' }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const PaymentForm = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [name, setName] = useState('');
  const [zip, setZip] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoStatus, setPromoStatus] = useState(null); // null | 'valid' | 'invalid'
  const [promoMessage, setPromoMessage] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);

  const [numberReady, setNumberReady] = useState(false);
  const [expiryReady, setExpiryReady] = useState(false);
  const [cvcReady, setCvcReady] = useState(false);

  const [focusedField, setFocusedField] = useState(null);

  const allReady = numberReady && expiryReady && cvcReady && name.trim() && zip.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !allReady) return;

    setLoading(true);
    setError('');

    const cardNumber = elements.getElement(CardNumberElement);

    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumber,
      billing_details: {
        name: name.trim(),
        address: { postal_code: zip.trim() },
      },
    });

    if (pmError) {
      setError(pmError.message);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API}/subscriptions/create-subscription`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
        payment_method_id: paymentMethod.id,
        ...(appliedPromo ? { promo_code: appliedPromo } : {}),
      }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }
      onSuccess();
    } catch (err) {
      setError('Could not connect to server. Please try again.');
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoStatus(null);
    setPromoMessage('');
    setAppliedPromo(null);
    try {
      const res = await fetch(`${API}/subscriptions/validate-promo`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code: promoCode.trim() }),
      });
      const data = await res.json();
      if (data.valid) {
        let msg = data.name;
        if (data.duration_in_months) msg += ` — ${data.duration_in_months} months free`;
        setPromoStatus('valid');
        setPromoMessage(msg);
        setAppliedPromo(promoCode.trim());
      } else {
        setPromoStatus('invalid');
        setPromoMessage('Promo code not found');
      }
    } catch {
      setPromoStatus('invalid');
      setPromoMessage('Could not validate code');
    } finally {
      setPromoLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <style>{AUTOFILL_OVERRIDE}</style>

      {/* Pricing messaging */}
      <div style={{ textAlign: 'center', marginBottom: '4px' }}>
        <p style={{
          fontFamily: "'Cormorant', Georgia, serif",
          fontStyle: 'italic',
          fontSize: '22px',
          color: 'rgba(255, 243, 225, 0.82)',
          lineHeight: 1.5,
          marginBottom: '6px',
        }}>
          Stay with Eunoia Presence.
        </p>
        <p style={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontSize: '15px',
          color: 'rgba(255, 243, 225, 0.40)',
          lineHeight: 1.6,
          margin: 0,
        }}>
          $7.99 / month · cancel anytime
        </p>
      </div>

      {/* Cardholder name */}
      <div style={focusedField === 'name' ? fieldFocused : fieldBase}>
        <input
          type="text"
          placeholder="Cardholder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={() => setFocusedField('name')}
          onBlur={() => setFocusedField(null)}
          data-testid="cardholder-name"
          style={textInputStyle}
          autoComplete="cc-name"
        />
      </div>

      {/* Card number */}
      <div style={focusedField === 'number' ? fieldFocused : fieldBase}>
        <CardNumberElement
          options={{ ...STRIPE_ELEMENT_STYLE, showIcon: true }}
          onFocus={() => setFocusedField('number')}
          onBlur={() => setFocusedField(null)}
          onChange={(e) => {
            setNumberReady(e.complete);
            if (e.error) setError(e.error.message);
            else setError('');
          }}
        />
      </div>

      {/* Expiry + CVC side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div style={focusedField === 'expiry' ? fieldFocused : fieldBase}>
          <CardExpiryElement
            options={STRIPE_ELEMENT_STYLE}
            onFocus={() => setFocusedField('expiry')}
            onBlur={() => setFocusedField(null)}
            onChange={(e) => {
              setExpiryReady(e.complete);
              if (e.error) setError(e.error.message);
              else setError('');
            }}
          />
        </div>
        <div style={focusedField === 'cvc' ? fieldFocused : fieldBase}>
          <CardCvcElement
            options={STRIPE_ELEMENT_STYLE}
            onFocus={() => setFocusedField('cvc')}
            onBlur={() => setFocusedField(null)}
            onChange={(e) => {
              setCvcReady(e.complete);
              if (e.error) setError(e.error.message);
              else setError('');
            }}
          />
        </div>
      </div>

      {/* Billing zip */}
      <div style={focusedField === 'zip' ? fieldFocused : fieldBase}>
        <input
          type="text"
          placeholder="Billing zip code"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 10))}
          onFocus={() => setFocusedField('zip')}
          onBlur={() => setFocusedField(null)}
          data-testid="billing-zip"
          style={textInputStyle}
          autoComplete="postal-code"
          inputMode="numeric"
        />
      </div>

      {/* Promo code */}
      {!showPromo ? (
        <button
          type="button"
          onClick={() => setShowPromo(true)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255, 243, 225, 0.30)',
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '14px',
            letterSpacing: '0.02em',
            padding: '0',
            textAlign: 'left',
          }}
        >
          Have a promo code?
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ ...fieldBase, flex: 1, padding: '12px 14px' }}>
              <input
                type="text"
                placeholder="Promo code"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setPromoStatus(null);
                  setAppliedPromo(null);
                }}
                style={{ ...textInputStyle, fontSize: '15px' }}
                data-testid="promo-code-input"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyPromo}
              disabled={promoLoading || !promoCode.trim()}
              style={{
                background: 'rgba(255, 243, 225, 0.06)',
                border: '1px solid rgba(255, 243, 225, 0.10)',
                borderRadius: '10px',
                padding: '12px 18px',
                cursor: promoLoading || !promoCode.trim() ? 'default' : 'pointer',
                color: 'rgba(255, 243, 225, 0.55)',
                fontFamily: "'EB Garamond', Georgia, serif",
                fontSize: '15px',
                letterSpacing: '0.02em',
                opacity: promoLoading || !promoCode.trim() ? 0.45 : 1,
                transition: 'opacity 200ms',
                whiteSpace: 'nowrap',
              }}
              data-testid="promo-apply-btn"
            >
              {promoLoading ? '...' : 'Apply'}
            </button>
          </div>

          {promoStatus === 'valid' && (
            <p style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '14px',
              color: 'rgba(196, 137, 74, 0.85)',
              margin: 0,
            }} data-testid="promo-success">
              ✓ {promoMessage} applied
            </p>
          )}
          {promoStatus === 'invalid' && (
            <p style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '14px',
              color: 'rgba(196, 137, 74, 0.45)',
              margin: 0,
            }} data-testid="promo-error">
              {promoMessage}
            </p>
          )}
        </div>
      )}

      {error && (
        <p style={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontSize: '14px',
          color: 'rgba(196, 137, 74, 0.85)',
          textAlign: 'center',
          margin: 0,
        }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!stripe || !allReady || loading}
        className="velvet-btn"
        data-testid="begin-trial-submit"
        style={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontSize: '17px',
          letterSpacing: '0.02em',
          opacity: (!stripe || !allReady || loading) ? 0.4 : 1,
          transition: 'opacity 240ms',
          marginTop: '4px',
        }}
      >
        {loading ? 'processing...' : 'Continue'}
      </button>

      {/* Trust elements */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', paddingTop: '4px' }}>
        {/* Stripe badge */}
        <a
          href="https://stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            textDecoration: 'none',
            opacity: 0.5,
          }}
        >
          <span style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '11px',
            color: 'rgba(255, 243, 225, 0.6)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            Powered by
          </span>
          <svg width="38" height="16" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a12.08 12.08 0 0 1-4.56.86c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.72zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.87zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zM14.56 20.3c-2.43 0-3.7-1.04-3.7-3.13 0-2.85 2.29-3.7 5.22-3.7h1.18v-.56c0-1.14-.38-1.77-1.6-1.77-1.26 0-2.67.5-3.81 1.17l-1.37-2.84c1.46-.9 3.62-1.52 5.63-1.52 3.23 0 5.18 1.54 5.18 4.87v7.19h-3.59l-.15-1.07c-.82.84-2.03 1.36-3 1.36zm1.55-4.85c-1.1 0-1.85.38-1.85 1.29 0 .75.52 1.1 1.35 1.1.71 0 1.35-.29 1.71-.77v-1.62h-1.21zM5.45 20.3c-2.04 0-4.09-.63-5.45-1.67l1.56-2.89c1.08.73 2.43 1.24 3.93 1.24 1.07 0 1.58-.4 1.58-1.02 0-.77-.69-1.04-2.29-1.54C2.6 13.88.5 12.95.5 10.16c0-2.75 2.2-4.86 5.77-4.86 1.83 0 3.47.5 4.72 1.33L9.5 9.5a7.26 7.26 0 0 0-3.27-.86c-.96 0-1.48.4-1.48.96 0 .69.58.96 2.19 1.46 2.75.86 4.74 1.87 4.74 4.68 0 2.98-2.33 4.56-6.23 4.56z" fill="rgba(255,243,225,0.7)"/>
          </svg>
        </a>

        {/* SSL line */}
        <p style={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontSize: '12px',
          color: 'rgba(255, 243, 225, 0.40)',
          margin: 0,
          textAlign: 'center',
        }}>
          <LockIcon />
          256-bit SSL encryption · Your information is secure
        </p>

        <LegalLinks style={{ marginTop: '12px' }} />
      </div>

    </form>
  );
};

const TrialPaymentView = ({ onSuccess }) => {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#080608',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 32px',
      overflowY: 'auto',
    }} data-testid="trial-payment-view">
      <div style={{ width: '100%', maxWidth: '400px' }} className="fade-in">

        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div className="breathing-sphere" style={{ margin: '0 auto 28px' }} />
          <h1 style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: '2rem',
            color: 'rgba(255, 243, 225, 0.82)',
            margin: 0,
          }}>
            Eunoia
          </h1>
        </div>

        <Elements stripe={stripePromise}>
          <PaymentForm onSuccess={onSuccess} />
        </Elements>

      </div>
    </div>
  );
};

export default TrialPaymentView;
